import { FC, useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, Typography, Alert } from '@mui/material';
import { RestartAlt } from '@mui/icons-material';
import React from 'react';
import { Modal } from '../../../dialogs/useModal';
import { useGuideCmsContext } from '../GuideCmsContext';
import { CREATE_NEW_GUIDE_CONTENT_VERSION, DISCARD_GUIDE_DRAFT } from '../content.graphql';
import { GUIDE_TIPTAP_DOCUMENT_STATUS } from '../../guide.graphql';
import { useMutation, useQuery } from '@apollo/client';
import { useUpdateNumberedNodesOnEditor, getNumberedNodes, getFullDescription } from '../tiptap/numbering';
import { yUndoPluginKey } from '@tiptap/y-tiptap';
import LoadingSpinner from '../../../LoadingSpinner';
import { watchPreviewContent } from '@tiptap-pro/extension-snapshot';
import { TiptapCollabProvider } from '@tiptap-pro/provider';
import { JSONContent } from '@tiptap/core';
import { TiptapDocumentDiff } from '../TiptapDocumentDiff';

export interface VersionCommentDialogDialogResult {}

export interface VersionCommentDialogProps {}

function getSavedVersionContent(provider: TiptapCollabProvider, version: number): Promise<JSONContent> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      unbind();
      reject(new Error('Tidsavbrudd ved henting av versjon'));
    }, 10000);

    const unbind = watchPreviewContent(provider, (content) => {
      unbind();
      clearTimeout(timeout);
      resolve(content as JSONContent);
    });

    provider.sendStateless(JSON.stringify({ action: 'version.preview', version }));
  });
}

export const VersionCommentDialog: FC<Modal<VersionCommentDialogProps, VersionCommentDialogDialogResult>> = (modal) => {
  const { editor, guideId, isArchive, provider, tiptapDocument, forceRefreshEditors, snapshotState } = useGuideCmsContext();
  const [isSaving, setIsSaving] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [isLoadingDiff, setIsLoadingDiff] = useState(false);
  const [diffError, setDiffError] = useState('');
  const [savedContent, setSavedContent] = useState<JSONContent | null>(null);
  const [createNewContentVersion] = useMutation(CREATE_NEW_GUIDE_CONTENT_VERSION);

  const [discardDraft] = useMutation(DISCARD_GUIDE_DRAFT);
  const [comment, setComment] = useState('');
  const { data: tiptapDocumentStatus, loading: isLoadingStatus } = useQuery(GUIDE_TIPTAP_DOCUMENT_STATUS, {
    variables: {
      documentName: tiptapDocument?.documentName,
    },
    fetchPolicy: 'network-only',
  });
  const isDirty = tiptapDocumentStatus?.guideTiptapDocumentStatus?.isDirty ?? true;
  const documentVersion = snapshotState.versions.length > 0 ? snapshotState.currentVersion : -1;
  const hasVersion = documentVersion > -1;

  const { close } = modal;
  const updateNumberedNodesOnEditor = useUpdateNumberedNodesOnEditor();

  const showDiffSection = isDirty && hasVersion && !isLoadingStatus;

  useEffect(() => {
    if (!editor || !provider || !isDirty || !hasVersion || isLoadingStatus) {
      setIsLoadingDiff(false);
      return;
    }
    setIsLoadingDiff(true);
    setDiffError('');
    setSavedContent(null);

    let cancelled = false;

    getSavedVersionContent(provider, documentVersion)
      .then((content) => {
        if (cancelled) return;
        setSavedContent(content);
        setIsLoadingDiff(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setDiffError(err.message);
        setIsLoadingDiff(false);
      });

    return () => {
      cancelled = true;
    };
  }, [editor, provider, isDirty, hasVersion, isLoadingStatus, documentVersion]);

  const handleOnClose = () => {
    close();
  };

  const handleOnUpdate = async () => {
    if (!editor) {
      return;
    }
    setIsSaving(true);
    const success = editor.commands.saveVersion(comment);
    if (!success) {
      setIsSaving(false);
      return;
    }
    if (isArchive) {
      setIsSaving(false);
      close({});
      return;
    }
    setTimeout(async () => {
      await createNewContentVersion({ variables: { input: { id: guideId, comment } } });
      setIsSaving(false);
      close({});
    }, 1000);
  };

  const handleDiscard = async () => {
    if (!editor) return;
    setIsDiscarding(true);
    editor.commands.clearContent();
    await discardDraft({ variables: { input: { documentName: tiptapDocument?.documentName } } });
    yUndoPluginKey.getState(editor.view.state)?.undoManager?.clear();
    updateNumberedNodesOnEditor(editor);
    forceRefreshEditors();
    close();
  };

  const currentContent = showDiffSection && !isLoadingDiff && savedContent ? editor?.getJSON() : null;

  const nodeLabels = React.useMemo(() => {
    if (!savedContent || !currentContent) return undefined;
    const labels = new Map<string, string>();
    for (const [id, node] of getNumberedNodes(currentContent)) {
      labels.set(id, getFullDescription(node));
    }
    for (const [id, node] of getNumberedNodes(savedContent)) {
      if (!labels.has(id)) {
        labels.set(id, getFullDescription(node));
      }
    }
    return labels;
  }, [savedContent, currentContent]);

  return (
    <Dialog open={true} onClose={handleOnClose} fullWidth maxWidth="md">
      <DialogTitle>Lagre ny versjon av innholdet</DialogTitle>
      <DialogContent>
        {isLoadingStatus || isDiscarding || isSaving ? (
          <LoadingSpinner size={40} />
        ) : !isDirty ? (
          <Alert severity="info">Innholdet har ingen endringer siden forrige versjon</Alert>
        ) : (
          <Stack spacing={2}>
            <div>
              <Typography variant="body1" gutterBottom>
                Legg til en beskrivelse av endringene dine
              </Typography>
              <TextField multiline minRows={4} fullWidth value={comment} onChange={(e) => setComment(e.target.value)} />
            </div>
            {showDiffSection && (
              <>
                {isLoadingDiff && <LoadingSpinner size={40} />}
                {diffError && <Alert severity="error">Klarte ikke vise endringer: {diffError}</Alert>}
                {savedContent && currentContent && (
                  <TiptapDocumentDiff
                    savedContent={savedContent}
                    currentContent={currentContent}
                    nodeLabels={nodeLabels}
                    label={
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Endringer siden versjon {documentVersion}
                      </Typography>
                    }
                  />
                )}
              </>
            )}
            {showDiscardConfirm ? (
              <Alert
                severity="warning"
                icon={<RestartAlt />}
                action={
                  <Stack direction="row" spacing={1}>
                    <Button size="small" disabled={isDiscarding} onClick={() => setShowDiscardConfirm(false)} variant="outlined" color="inherit">
                      Nei
                    </Button>
                    <Button size="small" color="error" variant="contained" disabled={isDiscarding} onClick={handleDiscard}>
                      {isDiscarding ? 'Forkaster...' : 'Ja, forkast'}
                    </Button>
                  </Stack>
                }>
                Forkaste alle endringer siden forrige versjon? Dette kan ikke angres.
              </Alert>
            ) : (
              showDiffSection && (
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  startIcon={<RestartAlt />}
                  disabled={isSaving}
                  onClick={() => setShowDiscardConfirm(true)}
                  sx={{ color: 'text.secondary', alignSelf: 'flex-start' }}>
                  Forkast endringer
                </Button>
              )
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {isLoadingStatus ? null : !isDirty ? (
          <Button variant="contained" onClick={handleOnClose}>
            Lukk
          </Button>
        ) : (
          <>
            <Button onClick={handleOnClose}>Avbryt</Button>
            <Button disabled={isSaving || showDiscardConfirm} variant="contained" onClick={handleOnUpdate}>
              Lagre
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
